import React, { useEffect, useMemo, useState } from "react";
import { Box, Dialog, IconButton, Typography, CircularProgress } from "@mui/material";
import { makeStyles } from "@Compat/makeStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";
import { fetchLegalDoc, renderLegalMarkdown } from "./renderLegalMarkdown";

const useStyles = makeStyles((theme) => ({
    paper: {
        width: 760,
        maxWidth: "100vw",
        height: "min(90vh, 900px)",
        borderRadius: 24,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        [theme.breakpoints.down("sm")]: {
            width: "100vw",
            height: "100vh",
            borderRadius: 0,
            margin: 0
        }
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: theme.spacing(2, 2.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        zIndex: 2
    },
    title: {
        fontWeight: 700,
        fontSize: "1.05rem"
    },
    body: {
        flex: 1,
        overflowY: "auto",
        padding: theme.spacing(3, 3.5),
        background: theme.palette.background.paper,
        "& h1, & h2, & h3, & h4": {
            lineHeight: 1.25,
            marginTop: theme.spacing(3),
            marginBottom: theme.spacing(1)
        },
        "& p, & li": {
            lineHeight: 1.6,
            fontSize: "0.95rem"
        },
        "& a": {
            color: theme.palette.primary.main
        },
        "& pre": {
            whiteSpace: "pre-wrap",
            background: theme.palette.action.hover,
            padding: theme.spacing(1.5),
            borderRadius: 6,
            overflowX: "auto"
        }
    },
    loadingOverlay: {
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: theme.palette.background.paper
    }
}));

interface LegalDocDialogProps {
    open: boolean;
    title: string;
    /** Active locale; the doc is fetched for it, falling back to English. */
    locale: string;
    /** Maps a locale → document URL, e.g. `(l) => `/legal/cookie-policy.${l}.md``. */
    buildUrl: (locale: string) => string;
    onClose: () => void;
}

/**
 * App-wide legal-document viewer: renders a sanitised /legal/*.md document in a
 * centred dialog. Mirrors the marketing-site LegalDrawer but works under the
 * main app theme too, so the cookie banner can surface policies on every page.
 */
const LegalDocDialog: React.FC<LegalDocDialogProps> = ({ open, title, locale, buildUrl, onClose }) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(true);
    const [text, setText] = useState("");

    useEffect(() => {
        let cancelled = false;
        if (!open) {
            return;
        }
        setLoading(true);
        setText("");
        fetchLegalDoc(buildUrl, locale)
            .then((md) => {
                if (!cancelled) {
                    setText(md);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [open, locale, buildUrl]);

    const html = useMemo(() => renderLegalMarkdown(text), [text]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ className: classes.paper }}>
            <div className={classes.header}>
                <Typography component="span" className={classes.title}>
                    {title}
                </Typography>
                <IconButton size="small" onClick={onClose} aria-label="Close">
                    <FontAwesomeIcon icon={faXmark} style={{ fontSize: 20 }} />
                </IconButton>
            </div>
            {loading ? (
                <div className={classes.loadingOverlay}>
                    <CircularProgress size={32} />
                </div>
            ) : (
                <Box className={classes.body} dangerouslySetInnerHTML={{ __html: html }} />
            )}
        </Dialog>
    );
};

export default LegalDocDialog;
